import type { FormBuilderField } from "@thalamos/aspire-common";

/**
 * The value keys a field's subtree reads, or `"all"` when they cannot be
 * bounded.
 *
 * A form draft would otherwise re-render every field on every keystroke, so a
 * field can be skipped when nothing it reads has changed. That is only safe if we know what
 * it reads, and the answer has to be conservative: a field wrongly skipped
 * shows a clinician stale information on a statutory form.
 *
 * Two things make the answer knowable, and both err towards rendering. A
 * field's definition names the keys it reads somewhere among its properties,
 * so every string in it is taken as one. And a template that hands a field a
 * function over the form's data (a `textFn`, a `disabledCondition`) has given
 * it reach that cannot be traced, so any field carrying one reads `"all"`.
 */
export type FieldDependencies = ReadonlySet<string> | "all";

type FieldType = FormBuilderField<Record<string, unknown>>["type"];

/**
 * What each field type reads beyond the keys named in its own definition.
 *
 * An entry lists the keys the component reads that the template has no reason
 * to name; anything the template does name is found by inspection. Nothing
 * checks an entry against its component, so adding one means reading that
 * component.
 *
 * `"unbounded"` means the field is never skipped: the answer both for a type
 * whose reads cannot be bounded by inspection — one addressing values by index
 * rather than by a named key — and for any type nobody has examined, since
 * {@link FieldDependencies} must err towards rendering.
 *
 * Total over the field types, so a type added later does not compile until
 * someone has said which it is.
 */
const READS_BEYOND_ITS_DEFINITION: Record<
  FieldType,
  "unbounded" | readonly string[]
> = {
  "multiple-copies": "unbounded",
  "amph-local-authority-picker": ["user"],
  "approved-clinician-confirmation-checkbox": ["user"],
  "section-12-confirmation-checkbox": ["user"],
  "confirmation-checkbox": [],
  "date-time": [],
  "hospital-picker": [],
  "local-authority-picker": [],
  "multi-select": [],
  "name-address": [],
  "place-of-safety-picker": [],
  branch: [],
  date: [],
  dropdown: [],
  label: [],
  practitioner: [],
  readonly: [],
  separator: [],
  textbox: [],
  time: [],
};

/** Computed once per field: template fields keep their identity for the run. */
const cache = new WeakMap<object, FieldDependencies>();

export function fieldDependencies<Data extends Record<string, unknown>>(
  field: FormBuilderField<Data>,
): FieldDependencies {
  const cached = cache.get(field);
  if (cached !== undefined) return cached;
  const dependencies = computeDependencies(field);
  cache.set(field, dependencies);
  return dependencies;
}

function computeDependencies<Data extends Record<string, unknown>>(
  field: FormBuilderField<Data>,
): FieldDependencies {
  const beyondDefinition = READS_BEYOND_ITS_DEFINITION[field.type];
  if (beyondDefinition === "unbounded") return "all";

  const keys = new Set<string>();
  if (!collectKeys(field, keys, new WeakSet())) return "all";
  for (const key of beyondDefinition) keys.add(key);

  // A branch renders the fields it reveals, so it reads whatever they read —
  // including the keys those fields never name.
  if (field.type === "branch") {
    for (const branch of field.branches) {
      for (const nested of [
        ...branch.fields,
        ...(branch.visibleFields ?? []),
      ]) {
        const nestedKeys = fieldDependencies(nested);
        if (nestedKeys === "all") return "all";
        for (const key of nestedKeys) keys.add(key);
      }
    }
  }

  return keys;
}

/**
 * Gathers every string in a field's definition as a key it might read,
 * returning false when the definition cannot be bounded at all.
 *
 * Deliberately over-collecting: labels and type names are gathered alongside
 * genuine keys. A watched string that names no value only ever compares
 * `undefined` to `undefined`, whereas missing one that does freezes the field
 * it belongs to — and a template names its keys in whatever property suits it,
 * including properties that only become keys when a component builds a nested
 * field out of them.
 */
function collectKeys(
  value: unknown,
  into: Set<string>,
  seen: WeakSet<object>,
): boolean {
  if (typeof value === "string") {
    into.add(value);
    return true;
  }
  if (typeof value === "function") return false;
  if (value === null || typeof value !== "object") return true;
  if (seen.has(value)) return true;
  seen.add(value);
  return Object.values(value).every((child) => collectKeys(child, into, seen));
}
